"use client";

import { type CSSProperties, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** How a data value was obtained. */
export type Provenance = "user-provided" | "estimated" | "benchmark" | "unset";

export const PROV_LABEL: Record<Provenance, string> = {
  "user-provided": "Provided",
  estimated: "Estimated",
  benchmark: "Benchmark",
  unset: "Unset",
};

export const PROV_TIP: Record<Provenance, string> = {
  "user-provided": "Inputted by someone at your org. Treated as ground truth.",
  estimated:
    "Based on Elion data, derived from your org's foundational inputs and patterns across similar orgs.",
  benchmark:
    "Industry benchmark default. Replace with org-specific data to tighten ROI math.",
  unset:
    "No value yet. Opportunities that depend on this field can't compute against it.",
};

/* -------------------------------------------------------------------------- */
/* Tailwind class maps per provenance kind                                     */
/* -------------------------------------------------------------------------- */

const PILL_CLS: Record<Provenance, string> = {
  "user-provided": [
    "bg-[color-mix(in_oklab,var(--brand-navy)_12%,transparent)]",
    "text-[var(--fg-brand)]",
    "border-[color-mix(in_oklab,var(--brand-navy)_30%,transparent)]",
  ].join(" "),
  estimated: [
    "bg-[color-mix(in_oklab,var(--accent-teal)_12%,transparent)]",
    "text-[var(--accent-teal-text)]",
    "border-[color-mix(in_oklab,var(--accent-teal)_30%,transparent)]",
  ].join(" "),
  benchmark: [
    "bg-[color-mix(in_oklab,var(--accent-gold)_15%,transparent)]",
    "text-[var(--accent-gold-text,var(--fg-secondary))]",
    "border-[color-mix(in_oklab,var(--accent-gold)_40%,transparent)]",
  ].join(" "),
  unset: [
    "bg-(--bg-canvas)",
    "text-(--fg-muted)",
    "border-(--border-default)",
  ].join(" "),
};

const TIP_WIDTH = 220;

export interface ProvenancePillProps {
  /** Provenance kind for the value. `undefined` renders as `unset`. */
  kind: Provenance | undefined;
}

/**
 * Small pill badge showing the provenance (source quality) of a data value.
 * Hover or focus to reveal a portal-rendered tooltip that explains the kind.
 */
export function ProvenancePill({ kind }: ProvenancePillProps) {
  const safe: Provenance = kind ?? "unset";
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const [tipStyle, setTipStyle] = useState<CSSProperties | null>(null);

  function show() {
    const rect = pillRef.current?.getBoundingClientRect();
    if (!rect) return;
    const right = Math.max(8, window.innerWidth - rect.right);
    setTipStyle({
      position: "fixed",
      bottom: window.innerHeight - rect.top + 8,
      right,
      width: TIP_WIDTH,
      maxWidth: "calc(100vw - 16px)",
      zIndex: 9999,
    });
  }
  const hide = () => setTipStyle(null);

  return (
    <>
      <button
        ref={pillRef}
        type="button"
        className={[
          "inline-flex items-center",
          "h-[18px] px-[7px]",
          "font-mono text-[9.5px] font-medium",
          "uppercase tracking-[0.1em]",
          "rounded-[3px] border",
          "cursor-help select-none",
          PILL_CLS[safe],
        ].join(" ")}
        aria-label={`Data provenance: ${PROV_LABEL[safe]}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {PROV_LABEL[safe]}
      </button>
      {tipStyle &&
        createPortal(
          <div
            className={[
              "absolute",
              "px-3 py-2.5",
              "bg-(--bg-white)",
              "border border-(--border-default)",
              "rounded-[3px]",
              "shadow-[0_8px_20px_rgba(10,16,32,0.12),0_2px_6px_rgba(10,16,32,0.06)]",
              "text-[11.5px] leading-[1.5]",
              "text-(--fg-secondary)",
              "pointer-events-none",
            ].join(" ")}
            style={tipStyle}
            role="tooltip"
          >
            {PROV_TIP[safe]}
          </div>,
          document.body,
        )}
    </>
  );
}
